/* eslint-disable react/require-default-props */
import React, {useMemo} from 'react';
import {View} from 'react-native';
import {isEmpty} from 'lodash';

import Icon from '/component/base/Icon';
import LoadingIndicator from '/component/base/LoadingIndicator/LoadingIndicator';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import TabLayout from '/component/layout/TabLayout';
import {DownlineProfile} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {NoOrder} from './FirstOrder/NoOrder';
import {useClientJourney} from './ClientJourney.hook';
import {styles} from './ClientJourney.style';
import {FirstOrder} from './FirstOrder';
interface CodeConfigs {
  timer: number;
}
interface ClientJourneyProps {
  clientId: number | null;
  onOrderPress?: () => void;
  getClientJourneyTasks?: () => void;
  getClientJourneyAllTasks?: () => void;
  getFirstOrder?: () => void;
  hasFirstOrder?: boolean;
  currentScreen?: string;
  hasFirstWeekTasks?: boolean;
  hasPreJourneyTasks?: boolean;
  hasWeeklyCheckInsTasks?: boolean;
  hasAllTasks?: boolean;
  isEmptyAllTasks?: boolean;
  onShowTaskDetail?: () => void;
  firstOrderShippingStatus?: string | null;
  orderId?: number | null;
  codeConfigs?: CodeConfigs;
  popoverShow?: () => void;
  clientJourneyTaskData?: Record<string, unknown>;
}

export const ClientJourney: React.FC<ClientJourneyProps> = ({
  clientId = null,
  onOrderPress = () => {},
  getClientJourneyTasks = () => {},
  getClientJourneyAllTasks = () => {},
  getFirstOrder = () => {},
  hasFirstOrder = false,
  currentScreen = '',
  hasFirstWeekTasks = false,
  hasPreJourneyTasks = false,
  hasWeeklyCheckInsTasks = false,
  hasAllTasks = false,
  isEmptyAllTasks = false,
  onShowTaskDetail = () => {},
  firstOrderShippingStatus = null,
  orderId: firstOrderId = null,
  codeConfigs = {timer: 10000},
  popoverShow = () => {},
  clientJourneyTaskData = {},
}) => {
  const {ts, tt} = useTranslation('screen.clients.section.clientJourney');
  const {
    clientJourneyExpanded,
    toggleExpand,
    tabHeight,
    tabs,
    currentInitialRouteName,
  } = useClientJourney({
    hasFirstWeekTasks,
    hasFirstOrder,
    hasPreJourneyTasks,
    hasWeeklyCheckInsTasks,
    clientId,
    onOrderPress,
    getClientJourneyTasks,
    getClientJourneyAllTasks,
    ts,
    firstOrderId,
    getFirstOrder,
    currentScreen,
    firstOrderShippingStatus,
    hasAllTasks,
    isEmptyAllTasks,
    onShowTaskDetail,
    remoteCodeConfig: codeConfigs,
    clientJourneyTaskData,
  });

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderTabs = useMemo(() => {
    if (clientJourneyExpanded && !isEmpty(tabs) && !isEmptyAllTasks) {
      const tabContainerStyle = {
        ...styles.tabContainerStyle,
        height: tabHeight,
      };
      return (
        <View style={tabContainerStyle}>
          <TabLayout
            initialRouteName={currentInitialRouteName}
            screenOptions={{
              lazy: true,
              lazyPlaceholder: () => (
                <LoadingIndicator style={styles.tabLoader} />
              ),
            }}
            swipeEnabled={true}
            style={{
              sceneContainerStyle: styles.sceneContainerStyle,
            }}
            tabs={tabs}
          />
        </View>
      );
    }
  }, [
    clientJourneyExpanded,
    tabs,
    isEmptyAllTasks,
    tabHeight,
    currentInitialRouteName,
  ]);

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderFirstOrderWidget = useMemo(() => {
    if (!firstOrderId && !isEmptyAllTasks) {
      return <NoOrder />;
    } else if (hasFirstOrder) {
      return (
        <FirstOrder
          onOrderPress={onOrderPress}
          clientId={clientId}
          orderId={firstOrderId}
          popoverShow={popoverShow}
        />
      );
    }
  }, [
    hasFirstOrder,
    firstOrderId,
    clientId,
    popoverShow,
    onOrderPress,
    isEmptyAllTasks,
  ]);

  const hasData = firstOrderId ? hasAllTasks && hasFirstOrder : hasAllTasks;

  if (!hasData) {
    return <LoadingIndicator size="small" style={styles.loader} />;
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text variant="heading" {...testProperties(DownlineProfile.cjTitle)}>
            {tt('heading', 'Client journey')}
          </Text>
        </View>
        <TouchableOpacity
          onPress={toggleExpand}
          style={styles.showHideButtonContainer}
          {...testProperties(null, true)}>
          <Text
            variant="body"
            disabled
            style={styles.showHideButton}
            {...testProperties(
              clientJourneyExpanded
                ? DownlineProfile.cjHideButton
                : DownlineProfile.cjShowButton,
            )}>
            {clientJourneyExpanded ? ts('hide', 'Hide') : ts('show', 'Show')}
          </Text>
          <Icon
            name={clientJourneyExpanded ? 'chevron-up' : 'chevron-down'}
            style={styles.icon}
          />
        </TouchableOpacity>
      </View>

      {clientJourneyExpanded && renderFirstOrderWidget}
      {renderTabs}
    </View>
  );
};
